<template>
  <div class="conversation-header pl-3 pl-sm-5 pt-3 pb-3 pr-3" :class="{'conversation-header-sticky': contact && isMobileViewport}">
    <div class="d-flex align-items-center justify-content-between h-100">
      <div class="flex-shrink-0 d-md-none">
        <button type="button"
                class="btn btn-secondary btn-block btn-md-inline mt-md-0 btn-back-feedback"
                @click="$emit('back')">
          <i class="fe fe-arrow-left"></i>
        </button>
      </div>

      <div class="flex-grow-1 text-center px-2" v-if="!selectedMessagesCount">
        <div class="d-flex flex-column align-items-center justify-content-center">
          <div class="d-flex align-items-center mb-0">
            <div class="online-status-wrapper">
              <i class="online-status"
                 :class="statusClass"
                 :title="statusTitle">
              </i>
            </div>

            <div class="ml-2 contact-name-wrapper">
              <a :href="profileUrl" class="text-dark font-weight-bold contact-name">
                <span class="contact-name-text">{{ contact?.full_name }}</span>
                <span v-if="contact?.age" class="contact-age">· {{ contact.age }}</span>
              </a>
            </div>
          </div>

          <small class="text-muted ml-2 contact-status">
            <span v-if="isTyping" class="typing-header-indicator font-italic">
              <span class="typing-header-indicator-text">{{ t('messages.typing') }}</span>
              <span class="typing-header-dots" aria-hidden="true">
                <span></span><span></span><span></span>
              </span>
            </span>
            <span v-else-if="onlineStatus === 'online'">{{ t('messages.online') }}</span>
            <span v-else-if="onlineStatus === 'recently_online'">{{ t('messages.just_online') }}</span>
            <span v-else>{{ lastSeenText || t('messages.offline') }}</span>
          </small>
        </div>
      </div>

      <div class="flex-grow-1 text-center px-2" v-if="selectedMessagesCount">
        {{ t('messages.selected_messages') }}: {{ selectedMessagesCount }}
      </div>

      <div class="flex-shrink-0" v-if="!selectedMessagesCount">
        <a class="avatar" href="#" role="button"
           @click.prevent="$emit('openMenu', $event)"
           :style="{ backgroundImage: `url(${contact?.avatar || ''})` }">
        </a>
      </div>

      <div class="flex-shrink-0" v-if="selectedMessagesCount">
        <button class="btn btn-danger btn-sm delete-selected-messages"
                @click="$emit('deleteSelectedMessages')"
                :title="t('messages.delete')">
          <i class="fe fe-trash"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from '../composables/useI18n.js';

const { t } = useI18n();

const props = defineProps<{
  contact?: any;
  onlineStatus?: string;
  isTyping?: boolean;
  lastOnlineText?: string;
  selectedMessagesCount?: number;
}>();


const onlineStatus = computed(() => props.onlineStatus || 'offline');
const isOnline = computed(() => onlineStatus.value === 'online');

const lastSeenText = computed(() => {
  // Ясная серверная формулировка ("Он был 6 часов назад") — приоритет над
  // компактным форматом сайдбара ("6 ч")
  if (props.lastOnlineText) {
    return props.lastOnlineText;
  }
  const lastSeen = props.contact?.last_seen ?? props.contact?.last_activity ?? null;
  if (!lastSeen) {
    return '';
  }
  const th = (window as any).TimezoneHelper;
  if (th && typeof th.formatRelativeTime === 'function') {
    return th.formatRelativeTime(parseInt(lastSeen, 10));
  }
  return '';
});

const statusClass = computed(() => {
  if (onlineStatus.value === 'online') return 'bg-green';
  if (onlineStatus.value === 'recently_online') return 'bg-orange';
  return 'bg-gray';
});

const statusTitle = computed(() => {
  if (onlineStatus.value === 'online') return t('messages.online');
  if (onlineStatus.value === 'recently_online') return t('messages.just_online');
  return lastSeenText.value || t('messages.offline');
});

const profileUrl = computed(() => {
  return props.contact?.url || '#';
});

const isMobileViewport = computed(() => window.innerWidth <= 768);
</script>

<style scoped>
.contact-name-wrapper {
  max-width: 200px;
  min-width: 0;
  flex: 1;
}

.contact-name {
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

.contact-name-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 150px;
  display: inline-block;
}

.contact-age {
  white-space: nowrap;
  flex-shrink: 0;
}

.contact-status {
  line-height: 1.2;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .contact-name-wrapper {
    max-width: 200px;
  }
  
  .contact-name-text {
    max-width: 160px;
  }
}

@media (max-width: 480px) {
  .contact-name-wrapper {
    max-width: 170px;
  }
  
  .contact-name-text {
    max-width: 130px;
  }
  
  .contact-age {
    font-size: 0.8em;
  }
}
</style>
